import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircleIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import { Tag } from '../ui/Tag';
import { EmptyState } from '../ui/EmptyState';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { formatDate } from '../../utils/format';
import type { ChatMessage, Role } from '../../types/marketplace';
interface ConversationListProps {
  role: Role;
  activeId?: string;
  basePath: string;
}
function preview(m?: ChatMessage): string {
  if (!m) return '';
  if (m.kind === 'song') return 'Shared a song';
  if (m.kind === 'offer') return 'Sent a booking offer';
  if (m.kind === 'confirmed') return 'Booking confirmed';
  return m.text ?? '';
}
export function ConversationList({
  role,
  activeId,
  basePath
}: ConversationListProps) {
  const {
    matches,
    messages,
    offers,
    getEvent,
    getPerformer,
    me
  } = useMarketplace();
  const rows = matches.filter(m => role === 'performer' ? m.performerId === me.id : getEvent(m.eventId)?.ownedByMe).map(m => {
    const list = messages[m.id] ?? [];
    const last = list[list.length - 1];
    return {
      match: m,
      last,
      at: last?.at ?? m.createdAt
    };
  }).sort((a, b) => b.at.localeCompare(a.at));
  if (rows.length === 0) {
    return <EmptyState icon={<MessageCircleIcon className="h-7 w-7" data-id="element-138" data-mp="xrzun6xiemkl" />} title="No conversations yet" body="When you match, a chat opens here so you can talk through the details." data-id="element-137" data-mp="xqostcnmjow4" />;
  }
  return <ul className="space-y-1 px-2" data-id="element-139" data-mp="1qflxue2trp6x">
      {rows.map(({
      match,
      last,
      at
    }) => {
      const event = getEvent(match.eventId);
      const performer = getPerformer(match.performerId);
      if (!event || !performer) return null;
      const offer = offers.filter(o => o.matchId === match.id).pop();
      const title = role === 'performer' ? event.name : performer.name;
      const sub = role === 'performer' ? event.organiser : event.name;
      const photo = role === 'performer' ? event.photos[0] : performer.photos[0];
      const unread = last && last.from !== role && last.from !== 'system';
      const active = activeId === match.id;
      return <li key={match.id} data-id="element-140" data-mp="1xylgghigpcp8">
            <Link to={`${basePath}/${match.id}`} aria-current={active ? 'page' : undefined} className={twMerge('flex items-center gap-3 rounded-md px-3 py-3 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand', active ? 'bg-surface-2' : 'hover:bg-surface-2/60')} data-id="element-141" data-mp="1xxtxq79yr9gd">
              <img src={photo} alt="" className="h-[52px] w-[52px] shrink-0 rounded-full object-cover" data-id="element-142" data-mp="1f2ltxm8971d1" />
              <div className="min-w-0 flex-1" data-id="element-143" data-mp="39etjsl5pws1y">
                <div className="flex items-center justify-between gap-2" data-id="element-144" data-mp="39dijwwfe84g4">
                  <p className="truncate font-bold" data-id="element-145" data-mp="35cylzqmzwwsx">{title}</p>
                  <span className="shrink-0 text-xs text-ink-3" data-id="element-146" data-mp="35bdsm4cf3jwn">{formatDate(at, 'd MMM')}</span>
                </div>
                <p className="truncate text-[13px] text-ink-3" data-id="element-147" data-mp="w4aavqd7q3w8">{sub}</p>
                <div className="mt-0.5 flex items-center gap-2" data-id="element-148" data-mp="w2fn84ak3xog">
                  <p className={twMerge('min-w-0 flex-1 truncate text-sm', unread ? 'font-bold text-ink' : 'text-ink-2')} data-id="element-149" data-mp="10wjy2ymy0put">{preview(last)}</p>
                  {offer?.status === 'accepted' && <Tag tone="success" className="h-6" data-id="element-150" data-mp="874kc13w8iv7">Booked</Tag>}
                  {offer?.status === 'pending' && <Tag tone="brand" className="h-6" data-id="element-151" data-mp="d564yqgywq8s">Offer</Tag>}
                  {unread && !offer && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-brand" aria-label="Unread" data-id="element-152" data-mp="h7j9xcnw4k4i" />}
                </div>
              </div>
            </Link>
          </li>;
    })}
    </ul>;
}